// ============================================================
// TOUCH CONTROLS
// Virtual D-pad and action buttons for mobile
// ============================================================

export interface TouchInputState {
    left: boolean;
    right: boolean;
    up: boolean;
    down: boolean;
    downJustPressed: boolean;
}

export class TouchControls {
    private scene: Phaser.Scene;
    private enabled: boolean = false;
    
    // Input state
    public state: TouchInputState = {
        left: false,
        right: false,
        up: false,
        down: false,
        downJustPressed: false,
    };
    
    // UI elements
    private dpadContainer!: Phaser.GameObjects.Container;
    private jumpButton!: Phaser.GameObjects.Arc;
    private actionButton!: Phaser.GameObjects.Arc;
    
    // Touch tracking
    private dpadTouch: number | null = null;
    private jumpTouch: number | null = null;
    private actionTouch: number | null = null;
    private lastDownState: boolean = false;
    
    // Layout constants
    private readonly DPAD_SIZE = 120;
    private readonly BUTTON_RADIUS = 40;
    private readonly MARGIN = 30;
    private readonly ALPHA = 0.4;
    private readonly ALPHA_PRESSED = 0.7;

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
        // Touch controls disabled
        this.enabled = false;
    }

    private detectMobile(): void {
        // Disabled - no touch controls
        this.enabled = false;
    }

    create(): void {
        if (!this.enabled) return;

        const { width, height } = this.scene.cameras.main;
        
        this.createDpad(this.MARGIN + this.DPAD_SIZE / 2, height - this.MARGIN - this.DPAD_SIZE / 2);
        this.createButtons(width - this.MARGIN - this.BUTTON_RADIUS, height - this.MARGIN - this.BUTTON_RADIUS);
        
        // Set up touch handlers
        this.scene.input.on('pointerdown', this.onPointerDown, this);
        this.scene.input.on('pointermove', this.onPointerMove, this);
        this.scene.input.on('pointerup', this.onPointerUp, this);
        
        // Handle resize
        this.scene.scale.on('resize', this.onResize, this);
    }

    private createDpad(x: number, y: number): void {
        this.dpadContainer = this.scene.add.container(x, y);
        this.dpadContainer.setScrollFactor(0);
        this.dpadContainer.setDepth(1000);

        const halfSize = this.DPAD_SIZE / 2;
        const third = this.DPAD_SIZE / 3;

        // Background circle
        const bg = this.scene.add.circle(0, 0, halfSize, 0x000000, this.ALPHA);
        this.dpadContainer.add(bg);

        // Direction indicators
        const arrowColor = 0xffffff;
        const arrowAlpha = 0.6;
        
        // Up arrow
        const upArrow = this.scene.add.triangle(0, -third, 0, -10, -10, 5, 10, 5, arrowColor, arrowAlpha);
        this.dpadContainer.add(upArrow);
        
        // Down arrow
        const downArrow = this.scene.add.triangle(0, third, 0, 10, -10, -5, 10, -5, arrowColor, arrowAlpha);
        this.dpadContainer.add(downArrow);
        
        // Left arrow
        const leftArrow = this.scene.add.triangle(-third, 0, -10, 0, 5, -10, 5, 10, arrowColor, arrowAlpha);
        this.dpadContainer.add(leftArrow);
        
        // Right arrow
        const rightArrow = this.scene.add.triangle(third, 0, 10, 0, -5, -10, -5, 10, arrowColor, arrowAlpha);
        this.dpadContainer.add(rightArrow);
    }

    private createButtons(x: number, y: number): void {
        // Jump button (top)
        this.jumpButton = this.scene.add.circle(x, y - this.BUTTON_RADIUS * 2.5, this.BUTTON_RADIUS, 0x00ff00, this.ALPHA);
        this.jumpButton.setScrollFactor(0);
        this.jumpButton.setDepth(1000);
        
        // Jump label
        const jumpLabel = this.scene.add.text(x, y - this.BUTTON_RADIUS * 2.5, 'JUMP', {
            fontSize: '14px',
            color: '#ffffff',
        }).setOrigin(0.5).setScrollFactor(0).setDepth(1001);

        // Action button (bottom) - for crouch/pickup/place
        this.actionButton = this.scene.add.circle(x, y, this.BUTTON_RADIUS, 0xff6600, this.ALPHA);
        this.actionButton.setScrollFactor(0);
        this.actionButton.setDepth(1000);
        
        // Action label
        const actionLabel = this.scene.add.text(x, y, 'ACT', {
            fontSize: '14px',
            color: '#ffffff',
        }).setOrigin(0.5).setScrollFactor(0).setDepth(1001);
    }

    private onPointerDown(pointer: Phaser.Input.Pointer): void {
        if (!this.enabled) return;

        const { x, y } = pointer;
        const { width, height } = this.scene.cameras.main;

        // Check D-pad
        const dpadX = this.MARGIN + this.DPAD_SIZE / 2;
        const dpadY = height - this.MARGIN - this.DPAD_SIZE / 2;
        const dpadDist = Phaser.Math.Distance.Between(x, y, dpadX, dpadY);
        
        if (dpadDist < this.DPAD_SIZE / 2) {
            this.dpadTouch = pointer.id;
            this.updateDpadState(x - dpadX, y - dpadY);
            return;
        }

        // Check jump button
        const jumpX = width - this.MARGIN - this.BUTTON_RADIUS;
        const jumpY = height - this.MARGIN - this.BUTTON_RADIUS - this.BUTTON_RADIUS * 2.5;
        const jumpDist = Phaser.Math.Distance.Between(x, y, jumpX, jumpY);
        
        if (jumpDist < this.BUTTON_RADIUS * 1.5) {
            this.jumpTouch = pointer.id;
            this.state.up = true;
            this.jumpButton.setAlpha(this.ALPHA_PRESSED);
            return;
        }

        // Check action button
        const actionX = width - this.MARGIN - this.BUTTON_RADIUS;
        const actionY = height - this.MARGIN - this.BUTTON_RADIUS;
        const actionDist = Phaser.Math.Distance.Between(x, y, actionX, actionY);
        
        if (actionDist < this.BUTTON_RADIUS * 1.5) {
            this.actionTouch = pointer.id;
            this.state.down = true;
            this.state.downJustPressed = !this.lastDownState;
            this.lastDownState = true;
            this.actionButton.setAlpha(this.ALPHA_PRESSED);
            return;
        }
    }

    private onPointerMove(pointer: Phaser.Input.Pointer): void {
        if (!this.enabled) return;

        // Update D-pad if this pointer owns it
        if (pointer.id === this.dpadTouch) {
            const { height } = this.scene.cameras.main;
            const dpadX = this.MARGIN + this.DPAD_SIZE / 2;
            const dpadY = height - this.MARGIN - this.DPAD_SIZE / 2;
            this.updateDpadState(pointer.x - dpadX, pointer.y - dpadY);
        }
    }

    private onPointerUp(pointer: Phaser.Input.Pointer): void {
        if (!this.enabled) return;

        if (pointer.id === this.dpadTouch) {
            this.dpadTouch = null;
            this.state.left = false;
            this.state.right = false;
        }

        if (pointer.id === this.jumpTouch) {
            this.jumpTouch = null;
            this.state.up = false;
            this.jumpButton.setAlpha(this.ALPHA);
        }

        if (pointer.id === this.actionTouch) {
            this.actionTouch = null;
            this.state.down = false;
            this.lastDownState = false;
            this.actionButton.setAlpha(this.ALPHA);
        }
    }

    private updateDpadState(dx: number, dy: number): void {
        const deadzone = 20;
        
        // Horizontal
        if (dx < -deadzone) {
            this.state.left = true;
            this.state.right = false;
        } else if (dx > deadzone) {
            this.state.left = false;
            this.state.right = true;
        } else {
            this.state.left = false;
            this.state.right = false;
        }

        // Vertical (up on dpad triggers jump too)
        if (dy < -deadzone) {
            this.state.up = true;
        }
    }

    private onResize(gameSize: Phaser.Structs.Size): void {
        const { width, height } = gameSize;
        
        // Reposition D-pad
        if (this.dpadContainer) {
            this.dpadContainer.setPosition(
                this.MARGIN + this.DPAD_SIZE / 2,
                height - this.MARGIN - this.DPAD_SIZE / 2
            );
        }

        // Reposition buttons
        const buttonX = width - this.MARGIN - this.BUTTON_RADIUS;
        if (this.jumpButton) {
            this.jumpButton.setPosition(buttonX, height - this.MARGIN - this.BUTTON_RADIUS - this.BUTTON_RADIUS * 2.5);
        }
        if (this.actionButton) {
            this.actionButton.setPosition(buttonX, height - this.MARGIN - this.BUTTON_RADIUS);
        }
    }

    // Call this each frame to clear just-pressed state
    clearJustPressed(): void {
        this.state.downJustPressed = false;
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    destroy(): void {
        this.scene.input.off('pointerdown', this.onPointerDown, this);
        this.scene.input.off('pointermove', this.onPointerMove, this);
        this.scene.input.off('pointerup', this.onPointerUp, this);
        this.scene.scale.off('resize', this.onResize, this);
    }
}
